import CrossIcon from "@atlaskit/icon/core/cross";
import LinkExternalIcon from "@atlaskit/icon/core/link-external";
import type {ReactNode} from "react";
import {twJoin} from "tailwind-merge";
import {AnankeButtonGroup} from "@vesta/ananke/components/anankeButtonGroup";
import {AnankeHeading} from "@vesta/ananke/components/anankeHeading";
import {AnankeIconButton} from "@vesta/ananke/components/anankeIconButton";
import {EntityProfileFooterBar} from "~/components/entityProfile/entityProfileFooterBar";
import {EntityProfileIdentityBlock} from "~/components/entityProfile/entityProfileIdentityBlock";
import {EntityProfileTabBar} from "~/components/entityProfile/entityProfileTabBar";
import {renderProfileTabContent} from "~/components/entityProfile/profileTabRegistry";
import type {EntityProfileControllerResult} from "~/components/entityProfile/types";
import {StandardModalHeaderDivider} from "~/components/standardModalHeader";

/**
 * Compact Lifecycle / entity profile shell for the aside column (Figma).
 * Owns header (more / expand / close), identity, tabs, and sticky footer.
 * Edit/create replace this shell entirely via {@link EntityProfileControllerResult.asideExclusiveSurface}.
 */
export function EntityProfileAsidePanel({
    controller,
    tabContext,
    title,
    headerTrailing,
    footer,
    onExpand,
    onClose,
    className,
}: {
    controller: EntityProfileControllerResult;
    tabContext: Parameters<typeof renderProfileTabContent>[0]["context"];
    title: string;
    /** Extra header actions before expand (typically more-menu). */
    headerTrailing?: ReactNode;
    /** When set, replaces the production footer action bar (config-driven CTAs). */
    footer?: ReactNode;
    onExpand?: () => void;
    onClose: () => void;
    className?: string;
}) {
    const {definition} = controller;

    // Exclusive surface: unmount profile chrome so edit/create cannot stack/overlap.
    if (controller.asideExclusiveSurface != null) {
        return (
            <div className={twJoin("relative h-full min-h-0 min-w-0 w-full overflow-hidden", className)}>
                {controller.asideExclusiveSurface}
                {controller.asideChrome}
            </div>
        );
    }

    const activeTabContent = renderProfileTabContent({
        tabId: controller.selectedTabId,
        kind: definition.kind,
        tabRenderProps: controller.tabRenderProps,
        context: tabContext,
    });

    return (
        <div
            className={twJoin(
                "relative grid h-full min-h-0 min-w-0 w-full overflow-hidden grid-rows-[auto_auto_minmax(0,1fr)_auto]",
                "bg-ds-elevation-surface",
                className,
            )}
        >
            <div className="shrink-0">
                <div className="grid w-full min-w-0 grid-cols-[minmax(0,1fr)_max-content] items-center gap-ds-space-200 px-ds-space-250 py-ds-space-150">
                    <div className="min-w-0 *:line-clamp-1">
                        <AnankeHeading size="small">{title}</AnankeHeading>
                    </div>
                    <AnankeButtonGroup>
                        {headerTrailing}
                        {onExpand === undefined ? null : (
                            <AnankeIconButton
                                type="button"
                                appearance="subtle"
                                icon={LinkExternalIcon}
                                label="Open full profile"
                                onClick={onExpand}
                            />
                        )}
                        <AnankeIconButton
                            type="button"
                            appearance="subtle"
                            icon={CrossIcon}
                            label="Close"
                            onClick={onClose}
                        />
                    </AnankeButtonGroup>
                </div>
                <StandardModalHeaderDivider />
            </div>

            <EntityProfileIdentityBlock
                summary={definition.summary}
                metaChips={definition.metaChips}
                className="shrink-0 px-ds-space-250 pt-ds-space-200 pb-ds-space-150"
            />

            <div className="min-h-0 h-full overflow-hidden grid grid-rows-[auto_minmax(0,1fr)] border-t border-solid border-ds-border">
                <div className="shrink-0 pt-ds-space-100">
                    <div className="px-ds-space-200">
                        <EntityProfileTabBar
                            tabs={definition.tabs}
                            selectedTabId={controller.selectedTabId}
                            onTabChange={controller.onTabChange}
                        />
                    </div>
                    <div className="h-px w-full bg-ds-border" />
                </div>
                <div className="relative min-h-0 h-full overflow-hidden">
                    <div className="absolute inset-0 min-h-0 min-w-0 overflow-hidden">{activeTabContent}</div>
                </div>
            </div>

            {footer !== undefined ? (
                footer
            ) : (
                <EntityProfileFooterBar
                    actionIds={definition.footerActionIds}
                    disabledActionIds={controller.footerDisabledActionIds}
                    onAction={controller.onFooterAction}
                    edgeToEdge={false}
                />
            )}

            {controller.asideChrome}
        </div>
    );
}
